import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, UserPlus, Save } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import StatusBadge from "@/components/StatusBadge";
import { endpoints, getRole } from "@/lib/api";
import { useLive } from "@/lib/useLive";

export default function RefereeAdmin() {
  const nav = useNavigate();
  const [teams, setTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [teamA, setTeamA] = useState("");
  const [teamB, setTeamB] = useState("");
  const [court, setCourt] = useState(1);
  const [target, setTarget] = useState(15);
  const [weekNum, setWeekNum] = useState("");
  const [creating, setCreating] = useState(false);
  const [editTeamId, setEditTeamId] = useState("");
  const editTeam = teams.find((t) => t.id === editTeamId) || null;

  useEffect(() => {
    const role = getRole();
    if (!role) { nav("/admin", { replace: true }); return; }
    if (role !== "admin") { nav("/referee", { replace: true }); toast.error("Admin access required"); }
  }, [nav]);

  const load = async () => {
    const [ts, fs] = await Promise.all([endpoints.teams(), endpoints.fixtures()]);
    setTeams(ts);
    setFixtures(fs);
  };
  useEffect(() => { load(); }, []);
  useLive(() => load());

  const create = async (e) => {
    e.preventDefault();
    if (!teamA || !teamB || teamA === teamB) {
      toast.error("Pick two different teams");
      return;
    }
    setCreating(true);
    try {
      await endpoints.createFixture({
        team_a_id: teamA,
        team_b_id: teamB,
        court_number: Number(court) || 1,
        target_score: Number(target) || 15,
        ...(weekNum ? { week_number: Number(weekNum) } : {}),
      });
      toast.success("Fixture created");
      setTeamA(""); setTeamB("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this fixture and all its matches?")) return;
    try { await endpoints.deleteFixture(id); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="app-shell">
      <AppHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 pb-12 pt-4">
        <Link
          to="/referee"
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground hover:text-primary"
          data-testid="back-to-referee-from-admin"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
        </Link>

        <h1 className="display text-3xl font-black uppercase tracking-tight">Admin</h1>
        <p className="mt-1 text-sm text-muted-foreground">Create fixtures. Each fixture spawns 4 rounds × 3 matches automatically.</p>

        <section className="mt-5 rounded-sm border border-border bg-card p-4">
          <h2 className="display text-lg font-black uppercase tracking-tight">New fixture</h2>
          <form className="mt-3 grid gap-3" onSubmit={create}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Team A">
                <select
                  value={teamA}
                  onChange={(e) => setTeamA(e.target.value)}
                  data-testid="new-team-a"
                  className="w-full rounded-sm border border-border bg-background px-2.5 py-2 text-sm"
                >
                  <option value="">Select team A</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Team B">
                <select
                  value={teamB}
                  onChange={(e) => setTeamB(e.target.value)}
                  data-testid="new-team-b"
                  className="w-full rounded-sm border border-border bg-background px-2.5 py-2 text-sm"
                >
                  <option value="">Select team B</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id} disabled={t.id === teamA}>{t.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Court">
                <input
                  type="number"
                  min={1}
                  value={court}
                  onChange={(e) => setCourt(e.target.value)}
                  data-testid="new-court"
                  className="w-full rounded-sm border border-border bg-background px-2.5 py-2 text-sm num-mono"
                />
              </Field>
              <Field label="Target score">
                <input
                  type="number"
                  min={1}
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  data-testid="new-target"
                  className="w-full rounded-sm border border-border bg-background px-2.5 py-2 text-sm num-mono"
                />
              </Field>
              <Field label="Week">
                <select
                  value={weekNum}
                  onChange={(e) => setWeekNum(e.target.value)}
                  data-testid="new-week"
                  className="w-full rounded-sm border border-border bg-background px-2.5 py-2 text-sm"
                >
                  <option value="">Auto (current week)</option>
                  {[1, 2, 3, 4, 5].map((w) => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                  <option value={6}>Week 6 · Playoffs</option>
                </select>
              </Field>
            </div>
            <div>
              <button
                type="submit"
                disabled={creating}
                data-testid="create-fixture-btn"
                className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-2 text-xs font-bold uppercase tracking-widest text-primary-foreground tap-feedback disabled:opacity-60"
              >
                <Plus className="h-3.5 w-3.5" /> {creating ? "Creating…" : "Create fixture"}
              </button>
            </div>
          </form>
        </section>

        <section className="mt-6 rounded-sm border border-border bg-card p-4" data-testid="player-manager">
          <h2 className="display text-lg font-black uppercase tracking-tight">Manage players</h2>
          <p className="mt-1 text-sm text-muted-foreground">Edit names, categories, contacts. Move players between teams. Add or remove.</p>
          <div className="mt-3">
            <Field label="Team">
              <select
                value={editTeamId}
                onChange={(e) => setEditTeamId(e.target.value)}
                data-testid="pm-team-select"
                className="w-full rounded-sm border border-border bg-background px-2.5 py-2 text-sm"
              >
                <option value="">Select team</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </Field>
          </div>
          {editTeam && (
            <div className="mt-4 grid gap-2">
              {(editTeam.players || []).map((p) => (
                <PlayerRow key={p.id} p={p} teams={teams} onChanged={load} />
              ))}
              <AddPlayerRow teamId={editTeam.id} onChanged={load} />
            </div>
          )}
        </section>

        <section className="mt-6">
          <h2 className="display text-xl font-black uppercase tracking-tight">All fixtures</h2>
          <div className="mt-3 grid gap-2">
            {fixtures.length === 0 && (
              <div className="rounded-sm border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
                No fixtures yet.
              </div>
            )}
            {fixtures.map((f) => (
              <div
                key={f.id}
                data-testid={`admin-fixture-${f.id}`}
                className="flex items-center justify-between rounded-sm border border-border bg-card px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={f.status} />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      Court {f.court_number} · Wk {f.week_number}
                    </span>
                  </div>
                  <div className="display text-base font-black uppercase tracking-tight">
                    {f.team_a_name} vs {f.team_b_name}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/referee/fixture/${f.id}`}
                    className="rounded-sm border border-border bg-card px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:border-primary/60"
                    data-testid={`admin-control-${f.id}`}
                  >
                    Control
                  </Link>
                  <button
                    onClick={() => remove(f.id)}
                    data-testid={`admin-delete-${f.id}`}
                    className="rounded-sm border border-border bg-card px-2 py-1.5 text-destructive hover:border-destructive"
                    aria-label="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

const CATEGORIES = ["beginner", "intermediate", "advance"];
const inputCls = "w-full rounded-sm border border-border bg-background px-2 py-1.5 text-sm";

function PlayerRow({ p, teams, onChanged }) {
  const [name, setName] = useState(p.name);
  const [contact, setContact] = useState(p.contact || "");
  const [category, setCategory] = useState(p.category || "beginner");
  const [teamId, setTeamId] = useState(p.team_id);
  const [saving, setSaving] = useState(false);

  const dirty =
    name.trim() !== p.name ||
    contact.trim() !== (p.contact || "") ||
    category !== (p.category || "beginner") ||
    teamId !== p.team_id;

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await endpoints.updatePlayer(p.id, {
        name: name.trim().toLowerCase(),
        contact: contact.trim(),
        category,
        team_id: teamId,
      });
      toast.success("Player updated");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${p.name} from the roster?`)) return;
    try {
      await endpoints.deletePlayer(p.id);
      toast.success("Player removed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div
      className="grid grid-cols-1 gap-2 rounded-sm border border-border bg-background p-2.5 sm:grid-cols-[1.4fr_1fr_0.9fr_0.9fr_auto]"
      data-testid={`pm-row-${p.id}`}
    >
      <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid={`pm-name-${p.id}`} />
      <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Contact" className={inputCls} data-testid={`pm-contact-${p.id}`} />
      <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} data-testid={`pm-category-${p.id}`}>
        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className={inputCls} data-testid={`pm-team-${p.id}`}>
        {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      <div className="flex items-center gap-1.5">
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid={`pm-save-${p.id}`}
          className="inline-flex h-8 items-center gap-1 rounded-sm bg-primary px-2.5 text-[11px] font-bold uppercase tracking-widest text-primary-foreground tap-feedback disabled:opacity-40"
        >
          <Save className="h-3 w-3" /> {saving ? "…" : "Save"}
        </button>
        <button
          onClick={remove}
          data-testid={`pm-delete-${p.id}`}
          className="inline-flex h-8 items-center rounded-sm border border-border px-2 text-destructive hover:border-destructive"
          aria-label="Remove player"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function AddPlayerRow({ teamId, onChanged }) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [category, setCategory] = useState("beginner");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await endpoints.createPlayer({
        name: name.trim().toLowerCase(),
        contact: contact.trim(),
        category,
        team_id: teamId,
      });
      toast.success("Player added");
      setName(""); setContact(""); setCategory("beginner");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="grid grid-cols-1 gap-2 rounded-sm border border-dashed border-primary/50 bg-primary/5 p-2.5 sm:grid-cols-[1.4fr_1fr_0.9fr_auto]"
      data-testid="pm-add-row"
    >
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New player name" className={inputCls} data-testid="pm-add-name" />
      <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Contact" className={inputCls} data-testid="pm-add-contact" />
      <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} data-testid="pm-add-category">
        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <button
        onClick={add}
        disabled={saving}
        data-testid="pm-add-btn"
        className="inline-flex h-8 items-center gap-1 rounded-sm bg-primary px-2.5 text-[11px] font-bold uppercase tracking-widest text-primary-foreground tap-feedback disabled:opacity-40"
      >
        <UserPlus className="h-3 w-3" /> {saving ? "…" : "Add"}
      </button>
    </div>
  );
}
